import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StudentSidebar from '../components/studentsidebar';
import Navbar from '../components/studentnavbar';
import { useSidebar } from '../components/SidebarContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/free-solid-svg-icons';
import { generateTest, createRazorpayOrder, getDepartments, checkPaymentRequirement, verifyPaymentAndGenerateTest } from '../services/testService';
import api from '../services/api';

const GenerateTestPage = () => {
  const [studentId, setStudentId] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedTimeDuration, setSelectedTimeDuration] = useState('60');
  const [departments, setDepartments] = useState([]);
  const [studentData, setStudentData] = useState(null);
  const [test, setTest] = useState(null);
  const [error, setError] = useState('');
  const [exhausted, setExhausted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [nextTestNumber, setNextTestNumber] = useState(1);
  const [pendingTests, setPendingTests] = useState([]);
  const navigate = useNavigate();
  const { isSidebarOpen } = useSidebar();
  const sidebarWidth = isSidebarOpen ? '15.6rem' : '4.4rem';

  const timeDurationOptions = [
    { value: '30', label: '30 Minutes (30 Questions)' },
    { value: '60', label: '60 Minutes (60 Questions)' },
    { value: '90', label: '90 Minutes (90 Questions)' },
    { value: '120', label: '120 Minutes (120 Questions)' }
  ];

  useEffect(() => {
    const storedStudentId = localStorage.getItem('id');
    if (storedStudentId) {
      setStudentId(storedStudentId);
      checkStudentPaymentStatus(storedStudentId);
      const storedStudent = localStorage.getItem('student');
      if (storedStudent) {
        try {
          setStudentData(JSON.parse(storedStudent));
        } catch (e) {
          console.error("Error parsing student data:", e);
          setError("Invalid student data in storage.");
        }
      }
    } else {
      setError("Student ID not found in storage. Please log in.");
    }
    loadDepartments();
    fetchPendingTests();
  }, []);

  const loadDepartments = async () => {
    try {
      const deptList = await getDepartments();
      setDepartments(deptList);
    } catch (err) {
      console.error("Error loading departments:", err);
      setError("Failed to load departments. Please try again.");
    }
  };

  const fetchPendingTests = async () => {
    try {
      const studentId = localStorage.getItem('id');
      if (!studentId) throw new Error("Student ID not found");
      const response = await api.get(`/api/test/pending/${studentId}`);
      const formattedTests = response.data.map((test, index) => ({
        id: test._id,
        testId: test._id,
        testName: test.testName || `Test ${index + 1}`,
        timeDuration: test.timeDuration,
        totalQuestions: test.totalQuestions,
        createdAt: test.createdAt,
        questions: test.questions,
      }));
      setPendingTests(formattedTests);
    } catch (err) {
      console.error('Error fetching pending tests:', err);
      setError("Failed to load pending tests. Please try again.");
      setPendingTests([]);
    }
  };

  const checkStudentPaymentStatus = async (id) => {
    try {
      setLoading(true);
      const status = await checkPaymentRequirement(id);
      setPaymentStatus(status);
      let calculatedNextTestNumber = 1;
      if (status.testsTaken !== undefined && status.testsTaken !== null) {
        calculatedNextTestNumber = status.testsTaken + 1;
      } else if (status.nextTestNumber !== undefined && status.nextTestNumber !== null) {
        calculatedNextTestNumber = status.nextTestNumber;
      }
      setNextTestNumber(calculatedNextTestNumber);
    } catch (err) {
      console.error("Error checking payment status:", err);
      setError("Failed to check payment status. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const navigateToTest = (testResult) => {
    sessionStorage.setItem('testNavigation', 'true');
    sessionStorage.setItem('testId', testResult.testId);
    sessionStorage.setItem('testData', JSON.stringify(testResult));
    navigate(`/take-test/${testResult.testId}`, {
      state: { 
        studentId,
        fromGenerate: true,
        testData: {
          ...testResult,
          timeDuration: testResult.timeDuration || parseInt(selectedTimeDuration)
        },
        timeDuration: testResult.timeDuration || parseInt(selectedTimeDuration)
      },
      replace: true
    });
  };

  const handleGenerate = async () => {
    if (!studentId.trim()) {
      setError("Please enter a valid Student ID");
      return;
    }
    if (!selectedDepartment) {
      setError("Please select a department");
      return;
    }
    if (!selectedTimeDuration) {
      setError("Please select a time duration");
      return;
    }
    if (pendingTests.length > 0) {
      setError("Please complete your previous test before creating a new one.");
      return;
    }
    try {
      setLoading(true);
      setError('');
      const paymentCheck = await checkPaymentRequirement(studentId);
      if (paymentCheck.paymentRequired) {
        setExhausted(true);
        setError(paymentCheck.message || "Payment required to generate a new test.");
      } else {
        if (studentData?.accountType === 'adminCreated' && paymentStatus?.testsTaken >= 2 && (!paymentStatus.isPaid || paymentStatus.isExpired)) {
          setExhausted(true);
          setError("You've used your 2 free tests. Pay ₹19.99 to unlock the next test.");
          return;
        } else if (studentData?.accountType === 'portalCreated' && (!paymentStatus?.isPaid || paymentStatus?.isExpired)) {
          setExhausted(true);
          setError("Payment required to access tests. Pay ₹19.99 to unlock the next test.");
          return;
        }
        const result = await generateTest(studentId, selectedDepartment, parseInt(selectedTimeDuration));
        if (!result || result.paymentRequired) {
          setExhausted(true);
          setError(result?.message || "Failed to generate test. Payment may be required.");
        } else {
          setTest(result);
          setError('');
          setExhausted(false);
          showStartTestPrompt(result, true);
        }
      }
    } catch (err) {
      console.error("Generate test error:", err);
      setError(err.message || "Failed to generate test. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!studentId.trim()) {
      setError("Please enter your Student ID before making a payment");
      return;
    }
    if (!selectedDepartment) {
      setError("Please select a department before making a payment");
      return;
    }
    if (!selectedTimeDuration) {
      setError("Please select a time duration before making a payment");
      return;
    }
    if (pendingTests.length > 0) {
      setError("Please complete your previous test before making a payment for a new one.");
      return;
    }
    try {
      setLoading(true);
      setError('');
      const res = await loadRazorpay('https://checkout.razorpay.com/v1/checkout.js');
      if (!res) throw new Error('Failed to load Razorpay SDK');
      const orderDetails = await createRazorpayOrder(studentId, 1999, selectedDepartment);
      const options = {
        key: orderDetails.key,
        amount: orderDetails.amount,
        currency: orderDetails.currency,
        order_id: orderDetails.orderId,
        name: 'Exam Portal',
        description: `Payment for ${selectedTimeDuration} Minute Test Access`,
        handler: async (response) => {
          try {
            if (!response.razorpay_signature) throw new Error('Payment signature missing');
            const verificationData = {
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature,
              studentId,
              departmentId: selectedDepartment,
              timeDuration: parseInt(selectedTimeDuration)
            };
            const verifyResponse = await fetch('/api/payments/verify-and-generate-test', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(verificationData)
            });
            const result = await verifyResponse.json();
            if (result.success) {
              const testResult = {
                testId: result.testId,
                questions: result.questions,
                testName: result.testName,
                timeDuration: result.timeDuration,
                totalQuestions: result.totalQuestions
              };
              setTest(testResult);
              setExhausted(false);
              setError('');
              await checkStudentPaymentStatus(studentId);
              showStartTestPrompt(testResult);
            } else {
              throw new Error(result.message || 'Payment verification failed');
            }
          } catch (err) {
            console.error("Payment verification error:", err);
            setError('Payment verification failed: ' + err.message);
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name: studentData?.fullName || '',
          email: studentData?.email || '',
          contact: studentData?.mobile || '',
        },
        theme: { color: '#3399cc' },
        modal: { ondismiss: () => setLoading(false) }
      };
      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (err) {
      console.error("Payment error:", err);
      setError('Payment failed: ' + (err.message || "Unknown error"));
      setLoading(false);
    }
  };

  const showStartTestPrompt = (testResult, isFreeTest = false) => {
    const modal = document.createElement('div');
    modal.id = 'start-test-modal';
    modal.style.cssText = `position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(0, 0, 0, 0.8); display: flex; align-items: center; justify-content: center; z-index: 10000; font-family: 'Roboto', sans-serif; overflow: hidden;`;
    const content = document.createElement('div');
    content.style.cssText = `background: white; padding: 40px; border-radius: 1rem; text-align: center; max-width: 500px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); background: linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF); font-family: 'Roboto', sans-serif; overflow: hidden;`;
    const successIcon = document.createElement('div');
    successIcon.innerHTML = '✅';
    successIcon.style.cssText = `font-size: 60px; margin-bottom: 20px; color: #6B21A8; font-family: 'Roboto', sans-serif;`;
    const title = document.createElement('h2');
    title.textContent = isFreeTest ? '🎉 Free Test Generated Successfully!' : '🎉 Payment Successful – You’re All Set!';
    title.style.cssText = `color: #6B21A8; margin-bottom: 15px; font-size: 24px; font-family: 'Roboto', sans-serif; font-weight: 500;`;
    const message = document.createElement('p');
    message.innerHTML = isFreeTest
      ? `Your ${testResult.timeDuration || selectedTimeDuration} minute free test is ready. Click "Start Test" to begin now or "Take Later" to return to the test generation page.`
      : `Your ${testResult.timeDuration || selectedTimeDuration} minute test is ready. A confirmation email has been sent to ${studentData?.email || 'your email'} and our support team at support@exam.doode.in. Click "Start Test" to begin now or "Take Later" to return to the test generation page.`;
    message.style.cssText = `color: #6B21A8; margin-bottom: 30px; font-size: 14px; line-height: 1.6; font-family: 'Roboto', sans-serif;`;
    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = `display: flex; justify-content: center; gap: 20px; font-family: 'Roboto', sans-serif;`;
    const startTestBtn = document.createElement('button');
    startTestBtn.textContent = 'Start Test';
    startTestBtn.style.cssText = `display: flex; align-items: center; justify-content: center; padding: 5px 15px; font-size: 14px; font-weight: 600; color: #6B21A8; text-transform: uppercase; letter-spacing: 1px; cursor: pointer; border-radius: 30px; border: 2px solid transparent; background-image: linear-gradient(#fff, #fff), linear-gradient(to right, #FFBBBB, #A8C1FF); background-origin: border-box; background-clip: padding-box, border-box; transition: all 0.4s ease; font-family: 'Roboto', sans-serif; outline: none;`;
    startTestBtn.onmouseover = () => { startTestBtn.style.transform = 'scale(1.05)'; startTestBtn.style.boxShadow = '0 0 12px rgba(130, 147, 183, 0.9), 0 0 18px rgba(187, 125, 245, 0.7)'; };
    startTestBtn.onmouseout = () => { startTestBtn.style.transform = 'scale(1)'; startTestBtn.style.boxShadow = 'none'; };
    startTestBtn.onclick = () => {
      document.body.removeChild(modal);
      navigateToTest(testResult);
    };
    const takeLaterBtn = document.createElement('button');
    takeLaterBtn.textContent = 'Take Later';
    takeLaterBtn.style.cssText = `display: flex; align-items: center; justify-content: center; padding: 5px 15px; font-size: 14px; font-weight: 600; color: #6B21A8; text-transform: uppercase; letter-spacing: 1px; cursor: pointer; border-radius: 30px; border: 2px solid transparent; background-image: linear-gradient(#fff, #fff), linear-gradient(to right, #FFBBBB, #A8C1FF); background-origin: border-box; background-clip: padding-box, border-box; transition: all 0.4s ease; font-family: 'Roboto', sans-serif; outline: none;`;
    takeLaterBtn.onmouseover = () => { takeLaterBtn.style.transform = 'scale(1.05)'; takeLaterBtn.style.boxShadow = '0 0 12px rgba(130, 147, 183, 0.9), 0 0 18px rgba(187, 125, 245, 0.7)'; };
    takeLaterBtn.onmouseout = () => { takeLaterBtn.style.transform = 'scale(1)'; takeLaterBtn.style.boxShadow = 'none'; };
    takeLaterBtn.onclick = () => {
      document.body.removeChild(modal);
      window.location.href = '/generate-test';
    };
    content.appendChild(successIcon);
    content.appendChild(title);
    content.appendChild(message);
    buttonContainer.appendChild(startTestBtn);
    buttonContainer.appendChild(takeLaterBtn);
    content.appendChild(buttonContainer);
    modal.appendChild(content);
    document.body.appendChild(modal);
    setTimeout(() => startTestBtn.focus(), 100);
  };

  const loadRazorpay = (src) =>
    new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const shouldShowPaymentPrompt = () => {
    if (exhausted) return true;
    if (!studentData || !paymentStatus) return false;
    if (studentData.accountType === 'adminCreated') return paymentStatus.testsTaken >= 2 && (!paymentStatus.isPaid || paymentStatus.isExpired);
    if (studentData.accountType === 'portalCreated') return !paymentStatus.isPaid || paymentStatus.isExpired;
    return false;
  };

  const paymentNoticeMessage = () => {
    if (!studentData) return "Payment required to access tests.";
    let testNumberToShow = nextTestNumber;
    if (paymentStatus && paymentStatus.testsTaken !== undefined) testNumberToShow = Math.max(nextTestNumber, paymentStatus.testsTaken + 1);
    if (studentData.accountType === 'adminCreated') return `You've used your 2 free tests. Pay ₹19.99 to unlock Next Test`;
    if (studentData.accountType === 'portalCreated') return `Payment required to access tests. No free tests are available. Pay ₹19.99 to unlock Next Test`;
    return `Payment required to access tests. Pay ₹19.99 to unlock Next Test`;
  };

  const pageStyle = { display: 'flex', minHeight: '100vh', background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)', fontFamily: "'Roboto', sans-serif", overflow: 'hidden' };
  const sidebarStyle = { position: 'fixed', top: 0, left: 0, height: '100%', zIndex: 10, width: sidebarWidth, transition: 'width 0.3s ease', fontFamily: "'Roboto', sans-serif", overflow: 'hidden' };
  const mainContentStyle = { marginLeft: sidebarWidth, width: `calc(100% - ${sidebarWidth})`, transition: 'all 0.3s ease', fontFamily: "'Roboto', sans-serif", overflow: 'hidden' };
  const contentStyle = { flex: 1, padding: '15px', margin: '6rem 2rem 2rem 2rem', backgroundColor: 'white', overflow: 'hidden', transition: 'margin-left 0.3s ease', minHeight: 'calc(100vh - 8rem)', borderTopLeftRadius: '1rem', borderTopRightRadius: '1rem', borderBottomLeftRadius: '1rem', borderBottomRightRadius: '1rem', maxWidth: `calc(100vw - ${sidebarWidth} - 4rem)`, width: '100%', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', fontFamily: "'Roboto', sans-serif" };
  const notificationStyle = { marginBottom: '1.5rem', padding: '0.75rem', backgroundColor: '#F3E8FF', color: '#6B21A8', borderRadius: '6px', display: 'flex', alignItems: 'center', fontFamily: "'Roboto', sans-serif", animation: 'slideInFromRight 0.5s ease-out' };
  const paymentNotificationStyle = { marginBottom: '0.5rem', padding: '1rem', backgroundColor: '#F3E8FF', color: '#6B21A8', borderRadius: '6px', fontFamily: "'Roboto', sans-serif", animation: 'slideInFromRight 0.5s ease-out', textAlign: 'center' };
  const buttonStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5px 15px', fontSize: '14px', fontWeight: '500', color: '#6B21A8', textTransform: 'uppercase', letterSpacing: '1px', cursor: 'pointer', borderRadius: '30px', border: '2px solid transparent', backgroundImage: 'linear-gradient(#fff, #fff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)', backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box', transition: 'all 0.4s ease', fontFamily: "'Roboto', sans-serif" };
  const paymentButtonContainerStyle = { display: 'flex', justifyContent: 'center', marginTop: '0.5rem', fontFamily: "'Roboto', sans-serif" };
  const tableContainer = { flex: 1, overflow: 'hidden', border: '1px solid #e5e7eb', borderRadius: '8px', marginTop: '2rem', height: 'auto' };
  const table = { width: '100%', borderCollapse: 'collapse', border: 'none', fontSize: '12px' };
  const row = { borderRadius: '8px' };
  const td = { padding: '10px 12px', verticalAlign: 'middle', textAlign: 'center', color: '#333', fontFamily: "'Roboto', sans-serif" };
  const actionButton = { backgroundColor: 'transparent', color: '#cc8888', border: 'none', borderRadius: '2px', padding: '2px', cursor: 'pointer', transition: 'color 0.3s ease, transform 0.3s ease' };

  return (
    <div style={pageStyle}>
      <style>{`
        @keyframes slideInFromRight { 0% { transform: translateX(100%); opacity: 0; } 100% { transform: translateX(0); opacity: 1; } }
        .glow-button:hover:not(:disabled) { background: linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF); transform: scale(1.05); animation: glow 1.5s ease-in-out infinite; font-family: 'Roboto', sans-serif !important; }
        .custom-table { border: none !important; }
        .custom-table th, .custom-table td { border: none !important; }
        .custom-table tbody tr:nth-child(odd) { background-color: #FFFFFF; }
        .custom-table tbody tr:nth-child(even) { background: linear-gradient(to right, #FAFCFF, #FBF6FF, #FFF8FB); }
        .custom-table tbody tr:hover { background: linear-gradient(to right, #F0F5FF, #F5EDFF, #FFF0F5) !important; }
        .action-button:hover { color: #b36666 !important; transform: scale(1.1); }
        .custom-table td:nth-child(2) { text-align: left !important; }
      `}</style>

      <div style={sidebarStyle}>
        <StudentSidebar />
      </div>

      <div style={mainContentStyle}>
        <Navbar />
        
        <div style={contentStyle}>
          <div style={{ maxWidth: '600px', margin: '0 auto', fontFamily: "'Roboto', sans-serif" }}>
            {paymentStatus && paymentStatus.isPaid && !paymentStatus.isExpired && (
              <div style={notificationStyle}>
                <span style={{ fontWeight: '500', fontFamily: "'Roboto', sans-serif" }}>
                  ✓ Premium access active until: {new Date(paymentStatus.expiryDate).toLocaleDateString()}
                </span>
              </div>
            )}

            {loading && (
              <div style={notificationStyle}>
                <span style={{ fontWeight: '500', fontFamily: "'Roboto', sans-serif" }}>
                  ⟳ Loading, please wait...
                </span>
              </div>
            )}

            <label htmlFor="studentId" style={{ display: 'block', fontSize: '1rem', color: '#6B21A8', marginBottom: '0.5rem', fontFamily: "'Roboto', sans-serif" }}>
              Student ID
            </label>
            <input
              id="studentId"
              type="text"
              value={studentId}
              disabled
              placeholder="Enter your Student ID"
              style={{ border: '1px solid #cbd5e1', borderRadius: '6px', padding: '0.6rem 0.75rem', width: '100%', marginBottom: '1rem', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box', backgroundColor: '#f1f5f9', color: '#64748b', fontFamily: "'Roboto', sans-serif" }}
            />

            <label htmlFor="department" style={{ display: 'block', fontSize: '1rem', color: '#6B21A8', marginBottom: '0.5rem', fontFamily: "'Roboto', sans-serif" }}>
              Department
            </label>
            <select
              id="department"
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              style={{ border: '1px solid #cbd5e1', borderRadius: '6px', padding: '0.6rem 0.75rem', width: '100%', marginBottom: '1rem', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box', backgroundColor: 'white', fontFamily: "'Roboto', sans-serif" }}
            >
              <option value="">Select a Department</option>
              {departments.map(dept => (
                <option key={dept._id} value={dept._id}>{dept.name}</option>
              ))}
            </select>

            <label htmlFor="timeDuration" style={{ display: 'block', fontSize: '1rem', color: '#6B21A8', marginBottom: '0.5rem', fontFamily: "'Roboto', sans-serif" }}>
              Test Duration
            </label>
            <select
              id="timeDuration"
              value={selectedTimeDuration}
              onChange={(e) => setSelectedTimeDuration(e.target.value)}
              style={{ border: '1px solid #cbd5e1', borderRadius: '6px', padding: '0.6rem 0.75rem', width: '100%', marginBottom: '0.75rem', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box', backgroundColor: 'white', fontFamily: "'Roboto', sans-serif" }}
            >
              {timeDurationOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>

            {!shouldShowPaymentPrompt() && (
              <button 
                onClick={handleGenerate} 
                style={{ ...buttonStyle, opacity: loading ? 0.7 : 1 }}
                disabled={loading}
                className="glow-button"
                aria-label="Create Test"
              >
                {loading ? 'Loading...' : 'Create Test'}
              </button>
            )}

            {error && !shouldShowPaymentPrompt() && (
              <div style={{ ...notificationStyle, marginTop: '1.25rem' }}>
                <span style={{ fontSize: '0.95rem', fontFamily: "'Roboto', sans-serif" }}>{error}</span>
              </div>
            )}

            {test && (
              <div style={{ ...notificationStyle, marginTop: '2rem' }}>
                <p style={{ marginBottom: '0.75rem', fontWeight: '500', fontFamily: "'Roboto', sans-serif" }}>
                  ✅ Test generated successfully! Redirecting to test...
                </p>
              </div>
            )}

            {shouldShowPaymentPrompt() && (
              <div>
                <div style={paymentNotificationStyle}>
                  <p style={{ marginBottom: '0', fontWeight: '500', fontFamily: "'Roboto', sans-serif" }}>
                    {paymentNoticeMessage()}
                  </p>
                </div>
                <div style={paymentButtonContainerStyle}>
                  <button 
                    onClick={handlePayment} 
                    style={{ ...buttonStyle, opacity: loading ? 0.7 : 1 }}
                    disabled={loading}
                    className="glow-button"
                    aria-label="Pay ₹19.99"
                  >
                    {loading ? 'Processing...' : 'Pay ₹19.99'}
                  </button>
                </div>
              </div>
            )}

            <div style={{ ...tableContainer, marginTop: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '8px', borderBottom: '1px solid #dee2e6' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '600', textAlign: 'center', color: '#552095', margin: 0, fontFamily: '"Roboto", sans-serif' }}>Test</h4>
              </div>
              {pendingTests.length === 0 ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', backgroundColor: '#ffffff', color: '#6B21A8', fontFamily: '"Roboto", sans-serif', animation: 'slideInFromRight 0.5s ease-out' }}>
                  <p>No pending tests found. Generate a new test to start.</p>
                </div>
              ) : (
                <table className="custom-table" style={table}>
                  <tbody>
                    {pendingTests.map((test, index) => (
                      <tr
                        key={test.id || index}
                        style={row}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(to right, #F0F5FF, #F5EDFF, #FFF0F5)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = index % 2 === 0 ? '#FFFFFF' : 'linear-gradient(to right, #FAFCFF, #FBF6FF, #FFF8FB)'; }}
                      >
                        <td style={{ ...td, textAlign: 'left' }}>{test.testName}</td>
                        <td style={td}>
                          <button
                            className="action-button"
                            style={actionButton}
                            onClick={() => navigateToTest(test)}
                            title="Start Test"
                          >
                            <FontAwesomeIcon icon={faPlay} style={{ fontSize: '17px' }} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateTestPage;